/**
 * employees.ts — Datos de empleados y asistencia por defecto para el modo demo.
 * Incluye trabajadores formales e informales (por día / por horas).
 */
import type { EmployeeExtended } from "@/features/payroll/types/payroll.types";
import type { Asistencia } from "@/features/personal/types/attendance";

const _ts = { seconds: 0, nanoseconds: 0 };

export const defaultEmployees: EmployeeExtended[] = [
  {
    id: "emp_1",
    businessId: "demo_business",
    createdAt: _ts,
    updatedAt: _ts,
    nombre: "Juan Pérez",
    email: "juan.perez@altenot.com",
    rol: "administrador" as const,
    cargo: "Director Ejecutivo",
    documento: "1098765432",
    telefono: "3001234567",
    salario: 6000000,
    salarioBase: 6000000,
    tipoContrato: "indefinido",
    tipoTrabajador: "formal",
    modalidadPago: "mensual",
    entidadSalud: "Sura",
    entidadPension: "Protección",
    isActive: true,
    horarioEntrada: "08:00",
    horarioSalida: "17:00",
    fechaIngreso: "2023-01-14",
    salaryHistory: [
      { monto: 4000000, fecha: "2023-01-14" },
      { monto: 6000000, fecha: "2025-01-14" },
    ],
    permissions: {
      dashboard: "edit",
      caja: "edit",
      caja_ventas: "edit",
      caja_cierres: "edit",
      caja_movimientos: "edit",
      caja_creditos: "edit",
      caja_fidelizacion: "edit",
      inventario: "edit",
      personal: "edit",
      personal_tareas: "edit",
      personal_asistencia: "edit",
      personal_nomina: "edit",
      personal_calendario: "edit",
      personal_vacantes: "edit",
      personal_buzon: "edit",
      higiene: "edit",
      auditoria: "edit",
      metricas: "edit",
      ia: "edit",
      tienda: "edit",
      tienda_liquidaciones: "edit",
      tienda_configuracion: "edit",
      calificaciones: "edit",
      mesas: "edit",
      mesas_configuracion: "edit",
      cocina: "edit",
    },
  },
  {
    id: "emp_2",
    businessId: "demo_business",
    createdAt: _ts,
    updatedAt: _ts,
    nombre: "María Rodríguez",
    email: "maria.r@altenot.com",
    rol: "supervisor" as const,
    cargo: "Gerente Operativa",
    documento: "1122334455",
    telefono: "3109876543",
    salario: 3500000,
    salarioBase: 3500000,
    tipoContrato: "indefinido",
    tipoTrabajador: "formal",
    modalidadPago: "mensual",
    entidadSalud: "Sanitas",
    entidadPension: "Colpensiones",
    isActive: true,
    horarioEntrada: "09:00",
    horarioSalida: "18:00",
    fechaIngreso: "2023-03-19",
    salaryHistory: [
      { monto: 3000000, fecha: "2023-03-19" },
      { monto: 3500000, fecha: "2025-03-19" },
    ],
    permissions: {
      dashboard: "view",
      caja: "view",
      caja_ventas: "edit",
      caja_cierres: "view",
      caja_movimientos: "view",
      caja_creditos: "none",
      caja_fidelizacion: "none",
      inventario: "edit",
      personal: "view",
      personal_tareas: "view",
      personal_asistencia: "view",
      personal_nomina: "none",
      personal_calendario: "none",
      personal_vacantes: "none",
      personal_buzon: "none",
      higiene: "none",
      auditoria: "none",
      metricas: "view",
      ia: "none",
      tienda: "none",
      tienda_liquidaciones: "none",
      tienda_configuracion: "none",
      calificaciones: "none",
      mesas: "view",
      mesas_configuracion: "none",
      cocina: "view",
    },
  },
  {
    id: "emp_3",
    businessId: "demo_business",
    createdAt: _ts,
    updatedAt: _ts,
    nombre: "Carlos Ruiz",
    email: "carlos.ruiz@altenot.com",
    rol: "vendedor" as const,
    cargo: "Asesor Comercial",
    documento: "9876543210",
    telefono: "3201112233",
    salario: 1300000,
    salarioBase: 1300000,
    tipoContrato: "fijo",
    tipoTrabajador: "formal",
    modalidadPago: "mensual",
    entidadSalud: "Sura",
    entidadPension: "Porvenir",
    isActive: true,
    horarioEntrada: "07:00",
    horarioSalida: "16:00",
    fechaIngreso: "2023-06-09",
    salaryHistory: [
      { monto: 1160000, fecha: "2023-06-09" },
      { monto: 1300000, fecha: "2024-01-01" },
    ],
    permissions: {
      dashboard: "none",
      caja: "edit",
      caja_ventas: "edit",
      caja_cierres: "none",
      caja_movimientos: "none",
      caja_creditos: "none",
      caja_fidelizacion: "none",
      inventario: "none",
      personal: "none",
      personal_tareas: "none",
      personal_asistencia: "none",
      personal_nomina: "none",
      personal_calendario: "none",
      personal_vacantes: "none",
      personal_buzon: "none",
      higiene: "none",
      auditoria: "none",
      metricas: "none",
      ia: "none",
      tienda: "none",
      tienda_liquidaciones: "none",
      tienda_configuracion: "none",
      calificaciones: "none",
      mesas: "edit",
      mesas_configuracion: "none",
      cocina: "view",
    },
  },
  {
    id: "emp_4",
    businessId: "demo_business",
    createdAt: _ts,
    updatedAt: _ts,
    nombre: "Sofia",
    email: "sofia@gmail.com",
    rol: "cajero" as const,
    cargo: "Cajera Principal",
    documento: "1055443322",
    telefono: "3142749518",
    salario: 2000000,
    salarioBase: 2000000,
    tipoContrato: "obra_labor",
    tipoTrabajador: "formal",
    modalidadPago: "mensual",
    entidadSalud: "Compensar",
    entidadPension: "Protección",
    isActive: true,
    horarioEntrada: "08:00",
    horarioSalida: "17:00",
    fechaIngreso: "2022-12-31",
    salaryHistory: [{ monto: 2000000, fecha: "2022-12-31" }],
    permissions: {
      dashboard: "none",
      caja: "edit",
      caja_ventas: "edit",
      caja_cierres: "none",
      caja_movimientos: "none",
      caja_creditos: "none",
      caja_fidelizacion: "none",
      inventario: "view",
      personal: "none",
      personal_tareas: "none",
      personal_asistencia: "none",
      personal_nomina: "none",
      personal_calendario: "none",
      personal_vacantes: "none",
      personal_buzon: "none",
      higiene: "none",
      auditoria: "none",
      metricas: "none",
      ia: "none",
      tienda: "none",
      tienda_liquidaciones: "none",
      tienda_configuracion: "none",
      calificaciones: "none",
      mesas: "none",
      mesas_configuracion: "none",
      cocina: "view",
    },
  },
  {
    id: "emp_5",
    businessId: "demo_business",
    createdAt: _ts,
    updatedAt: _ts,
    nombre: "Diego Herrera",
    email: "",
    rol: "cajero" as const,
    cargo: "Obrero de Mantenimiento",
    documento: "1033221144",
    telefono: "3178889900",
    salario: 0,
    salarioBase: 0,
    valorDia: 75000,
    tipoTrabajador: "informal",
    modalidadPago: "diario",
    isActive: true,
    horarioEntrada: "07:00",
    horarioSalida: "16:00",
    fechaIngreso: "2025-02-10",
    salaryHistory: [],
    permissions: {
      dashboard: "none",
      caja: "none",
      caja_ventas: "none",
      caja_cierres: "none",
      caja_movimientos: "none",
      caja_creditos: "none",
      caja_fidelizacion: "none",
      inventario: "none",
      personal: "none",
      personal_tareas: "none",
      personal_asistencia: "none",
      personal_nomina: "none",
      personal_calendario: "none",
      personal_vacantes: "none",
      personal_buzon: "none",
      higiene: "none",
      auditoria: "none",
      metricas: "none",
      ia: "none",
      tienda: "none",
      tienda_liquidaciones: "none",
      tienda_configuracion: "none",
      calificaciones: "none",
      mesas: "none",
      mesas_configuracion: "none",
      cocina: "view",
    },
  },
  {
    id: "emp_6",
    businessId: "demo_business",
    createdAt: _ts,
    updatedAt: _ts,
    nombre: "Luisa Martínez",
    email: "",
    rol: "vendedor" as const,
    cargo: "Auxiliar Temporal",
    documento: "1077665544",
    telefono: "3204455667",
    salario: 0,
    salarioBase: 0,
    valorHora: 12000,
    tipoTrabajador: "informal",
    modalidadPago: "por_horas",
    isActive: true,
    horarioEntrada: "08:00",
    horarioSalida: "12:00",
    fechaIngreso: "2025-04-01",
    salaryHistory: [],
    permissions: {
      dashboard: "none",
      caja: "none",
      caja_ventas: "none",
      caja_cierres: "none",
      caja_movimientos: "none",
      caja_creditos: "none",
      caja_fidelizacion: "none",
      inventario: "none",
      personal: "none",
      personal_tareas: "none",
      personal_asistencia: "none",
      personal_nomina: "none",
      personal_calendario: "none",
      personal_vacantes: "none",
      personal_buzon: "none",
      higiene: "none",
      auditoria: "none",
      metricas: "none",
      ia: "none",
      tienda: "none",
      tienda_liquidaciones: "none",
      tienda_configuracion: "none",
      calificaciones: "none",
      mesas: "none",
      mesas_configuracion: "none",
      cocina: "view",
    },
  },
];

/**
 * defaultAttendance — Registros de asistencia por defecto (modo demo).
 *
 * Genera un histórico realista de las últimas dos semanas para toda la nómina:
 * turnos cerrados y abiertos (hoy), fichajes puntuales y con retraso, distintos
 * métodos (QR/facial/carnet) y evidencia fotográfica en los registros faciales.
 * La estructura coincide con la que esperan el frontend (tipo `Asistencia`) y
 * los handlers mock.
 *
 * Los datos se calculan al cargar el módulo a partir de `new Date()`, por lo que
 * «hoy» siempre tiene fichajes y los KPIs de la página no quedan en cero.
 */
interface DemoAttendanceSchedule {
  id: string;
  nombre: string;
  entrada: [number, number];
  salida: [number, number];
}

/** Horario de referencia por empleado (debe coincidir con defaultEmployees). */
const DEMO_ATTENDANCE_SCHEDULES: DemoAttendanceSchedule[] = [
  { id: "emp_1", nombre: "Juan Pérez", entrada: [8, 0], salida: [17, 0] },
  { id: "emp_2", nombre: "María Rodríguez", entrada: [9, 0], salida: [18, 0] },
  { id: "emp_3", nombre: "Carlos Ruiz", entrada: [7, 0], salida: [16, 0] },
  { id: "emp_4", nombre: "Sofia", entrada: [8, 0], salida: [17, 0] },
  { id: "emp_5", nombre: "Diego Herrera", entrada: [7, 0], salida: [16, 0] },
  { id: "emp_6", nombre: "Luisa Martínez", entrada: [8, 0], salida: [12, 0] },
];

/** Empleados con turno abierto hoy (el resto ya cerró) para ver «EN TURNO». */
const DEMO_OPEN_TODAY = new Set(["emp_1", "emp_2", "emp_4"]);

const DEMO_ATTENDANCE_METHODS = ["qr", "facial", "carnet"] as const;
type DemoAttendanceMethod = (typeof DEMO_ATTENDANCE_METHODS)[number];

/** Tolerancia por defecto de la política de tardanzas (minutos). */
const DEMO_TOLERANCIA_MINUTOS = 5;
/** Días de histórico hacia atrás (además de hoy). */
const DEMO_DIAS_HISTORICO = 14;

/**
 * Evidencia de ejemplo (SVG embebido) para los fichajes faciales: permite abrir
 * el visor de evidencia del grid/tabla sin depender de Firebase Storage.
 */
const EVIDENCIA_DEMO_DATA_URI =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="480" height="360">' +
      '<rect width="480" height="360" fill="#e2e8f0"/>' +
      '<circle cx="240" cy="140" r="58" fill="#94a3b8"/>' +
      '<path d="M140 320c0-55 45-100 100-100s100 45 100 100z" fill="#94a3b8"/>' +
      '<text x="240" y="348" font-family="sans-serif" font-size="16" fill="#475569" text-anchor="middle">Evidencia demo</text>' +
      "</svg>",
  );

/** PRNG determinista (mulberry32 sobre hash FNV-1a) para variar sin Math.random. */
function createSeededRandom(seed: string): () => number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return () => {
    h += 0x6d2b79f5;
    let t = h;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

function toDateKey(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function pickMethod(random: () => number): DemoAttendanceMethod {
  const index = Math.floor(random() * DEMO_ATTENDANCE_METHODS.length);
  return DEMO_ATTENDANCE_METHODS[index] ?? "qr";
}

function buildDemoAttendanceRecord(params: {
  schedule: DemoAttendanceSchedule;
  day: Date;
  entradaOffsetMin: number;
  /** `null` deja el turno abierto (sin hora de salida). */
  salidaOffsetMin: number | null;
  metodo: DemoAttendanceMethod;
}): Asistencia {
  const { schedule, day, entradaOffsetMin, salidaOffsetMin, metodo } = params;
  const fecha = toDateKey(day);

  const horaEntrada = new Date(day);
  horaEntrada.setHours(
    schedule.entrada[0],
    schedule.entrada[1] + entradaOffsetMin,
    0,
    0,
  );

  let horaSalida: Date | null = null;
  if (salidaOffsetMin !== null) {
    horaSalida = new Date(day);
    horaSalida.setHours(
      schedule.salida[0],
      schedule.salida[1] + salidaOffsetMin,
      0,
      0,
    );
  }

  const llegoATiempo = entradaOffsetMin <= DEMO_TOLERANCIA_MINUTOS;
  const evidencia = metodo === "facial" ? EVIDENCIA_DEMO_DATA_URI : null;
  const duracionMinutos = horaSalida
    ? Math.round((horaSalida.getTime() - horaEntrada.getTime()) / 60000)
    : null;

  return {
    id: `att_${schedule.id}_${fecha}`,
    empleadoId: schedule.id,
    empleadoNombre: schedule.nombre,
    fecha,
    horaEntrada: horaEntrada.toISOString(),
    horaSalida: horaSalida ? horaSalida.toISOString() : undefined,
    llegoATiempo,
    minutosRetraso: llegoATiempo ? 0 : entradaOffsetMin,
    accionRetraso: llegoATiempo ? undefined : "advertir",
    metodo,
    metodoSalida: horaSalida ? metodo : undefined,
    duracionMinutos,
    fotoVerificacionEntrada: evidencia,
    fotoVerificacionSalida: horaSalida ? evidencia : null,
    createdAt: horaEntrada.toISOString(),
    updatedAt: (horaSalida ?? horaEntrada).toISOString(),
  };
}

function buildDefaultAttendance(): Asistencia[] {
  const now = new Date();
  const registros: Asistencia[] = [];

  for (let offset = 0; offset <= DEMO_DIAS_HISTORICO; offset += 1) {
    const day = new Date(now);
    day.setHours(12, 0, 0, 0);
    day.setDate(day.getDate() - offset);
    if (day.getDay() === 0) continue; // domingo: no se ficha

    DEMO_ATTENDANCE_SCHEDULES.forEach((schedule, index) => {
      const random = createSeededRandom(`${schedule.id}-${toDateKey(day)}`);

      if (offset === 0) {
        // Hoy: unos turnos siguen abiertos (EN TURNO) y otros ya cerraron.
        // Carlos (emp_3, sesión EMPLEADO DEMO) cierra el suyo para poder probar
        // el fichaje de entrada desde /mi-asistencia.
        registros.push(
          buildDemoAttendanceRecord({
            schedule,
            day,
            entradaOffsetMin: index === 1 ? 12 : Math.round(random() * 6) - 2,
            salidaOffsetMin: DEMO_OPEN_TODAY.has(schedule.id)
              ? null
              : Math.round(random() * 8) - 2,
            metodo: pickMethod(random),
          }),
        );
        return;
      }

      // Ausencia esporádica para que el histórico no sea uniforme.
      if (random() < 0.06) return;

      const esTarde = random() < 0.22;
      const entradaOffsetMin = esTarde
        ? 6 + Math.round(random() * 24)
        : Math.round(random() * 11) - 10;

      registros.push(
        buildDemoAttendanceRecord({
          schedule,
          day,
          entradaOffsetMin,
          salidaOffsetMin: Math.round(random() * 22) - 6,
          metodo: pickMethod(random),
        }),
      );
    });
  }

  return registros;
}

export const defaultAttendance: Asistencia[] = buildDefaultAttendance();
